import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { del, get, set } from 'idb-keyval';
import { ApiError, NetworkError } from './api';
import { reportClientError } from './install-error-reporter';

// One client per app lifetime. 30s staleTime is the sweet spot for a
// food-logging UI — dials refresh on window focus without hammering the
// API, and mutations trigger deliberate invalidations where needed.
//
// Persisted queries survive a 24h window, which matches our retention
// goal for the offline read of "today" + the most recent few days.
const PERSIST_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const PERSIST_KEY = 'food-diary-query-cache';

// Forward unhandled query/mutation failures to the client-error beacon
// so we can diagnose UI-visible errors after the fact. Skip 401s — those
// are the WorkOS reauth path and would just spam.
function reportQueryError(kind: 'query' | 'mutation', error: unknown): void {
  if (error instanceof ApiError && error.status === 401) return;
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;
  reportClientError({
    kind,
    message,
    stack,
    ...(error instanceof ApiError
      ? {
          httpStatus: error.status,
          requestId: error.requestId,
          method: error.method,
          apiPath: error.path,
        }
      : {}),
    // NetworkError fires when `fetch()` rejects before a response arrived
    // (CORS abort, tab suspended, DNS hiccup) OR when an NDJSON stream is
    // torn down mid-body. The mid-body variant also carries cf-ray.
    ...(error instanceof NetworkError
      ? {
          method: error.method,
          apiPath: error.path,
          ...(error.requestId === undefined ? {} : { requestId: error.requestId }),
        }
      : {}),
  });
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        reportQueryError('query', error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        reportQueryError('mutation', error);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // Keep cached responses around long enough for the persister to
        // hydrate them on a cold offline start.
        gcTime: PERSIST_MAX_AGE_MS,
        retry: (failureCount, error) => {
          if (error instanceof Error && error.message.startsWith('API 4')) return false;
          return failureCount < 2;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });
}

// AsyncStorage adapter over idb-keyval — IndexedDB is the only browser
// store with enough headroom for our entire query cache.
const idbStorage = {
  getItem: (key: string): Promise<string | null> => get<string>(key).then((value) => value ?? null),
  setItem: (key: string, value: string): Promise<void> => set(key, value),
  removeItem: (key: string): Promise<void> => del(key),
};

export function createPersister(): ReturnType<typeof createAsyncStoragePersister> {
  return createAsyncStoragePersister({
    storage: idbStorage,
    key: PERSIST_KEY,
    throttleTime: 1000,
  });
}

export const PERSIST_OPTIONS = {
  maxAge: PERSIST_MAX_AGE_MS,
  buster: 'v1',
} as const;
